import { FC, useRef, useMemo } from 'react';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { AppPage, AppCard } from '../../components/general';
import CalendarRange, { DefaultRangeSelectType } from '../../components/common/CalendarRange';
import { VoxRoiResult } from '../../models/voxRoi';
import styles from './VoxRoi.module.css';

export interface VoxRoiProps {
  status: 'locked' | 'unlocked';
  hasConfig: boolean;
  passphrase: string;
  onPassphraseChange: (value: string) => void;
  onUnlock: () => void;
  unlockError: string | null;
  isConfigLoading: boolean;
  dateRange: Date[] | undefined;
  onDateRangeChange: (range: Date[] | undefined) => void;
  result: VoxRoiResult | null;
  isUsageLoading: boolean;
  onEditConfig: () => void;
}

const usd = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const usd2 = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const pct = (value: number | null) =>
  value === null ? '—' : `${(value * 100).toFixed(1)}%`;

const breadcrumbs = [
  { label: 'Dashboard', to: '/dashboard' },
  { label: 'VoX ROI Savings' },
];

const rangeOptions = [
  DefaultRangeSelectType.CurrentMonth,
  DefaultRangeSelectType.Past30Days,
  DefaultRangeSelectType.Past2Weeks,
  DefaultRangeSelectType.PastWeek,
];

export const VoxRoi: FC<VoxRoiProps> = ({
  status,
  hasConfig,
  passphrase,
  onPassphraseChange,
  onUnlock,
  unlockError,
  isConfigLoading,
  dateRange,
  onDateRangeChange,
  result,
  isUsageLoading,
  onEditConfig,
}) => {
  const unmappedUsage = result?.warnings.unmappedUsage;
  const loggedUnmappedRef = useRef<unknown>(null);
  if (unmappedUsage && unmappedUsage.length > 0 && loggedUnmappedRef.current !== unmappedUsage) {
    loggedUnmappedRef.current = unmappedUsage;
    console.warn(
      `${unmappedUsage.length} usage row(s) could not be mapped to a catalog product:`,
      unmappedUsage,
    );
    console.table(unmappedUsage);
  }

  if (status === 'locked') {
    return (
      <AppPage title="VoX ROI Savings" breadcrumbs={breadcrumbs}>
        <AppCard title={hasConfig ? 'Unlock your ROI report' : 'Set up your ROI configuration'}>
          <div className="flex flex-column gap-3" style={{ maxWidth: 420 }}>
            <p className="text-500 text-sm m-0">
              Your cost configuration is encrypted. Enter your passphrase to {hasConfig ? 'unlock' : 'create'} it.
              Mezzo cannot read your costs and cannot recover a lost passphrase.
            </p>
            <label htmlFor="vox-roi-passphrase" className="font-semibold text-sm">Passphrase</label>
            <div className={styles.unlockFieldGroup}>
              <Password
                inputId="vox-roi-passphrase"
                value={passphrase}
                onChange={(event) => onPassphraseChange(event.target.value)}
                feedback={!hasConfig}
                toggleMask
                onKeyDown={(event) => { if (event.key === 'Enter') onUnlock(); }}
              />
              {unlockError && <Message severity="error" text={unlockError} />}
              <Button label={hasConfig ? 'Unlock' : 'Continue setup'} icon="pi pi-lock-open" onClick={onUnlock} className="w-auto" disabled={isConfigLoading} loading={isConfigLoading} />
            </div>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <AppPage
      title="VoX ROI Savings"
      breadcrumbs={breadcrumbs}
      headerEnd={
        <div className="flex align-items-center gap-3">
          <CalendarRange rangeSelectOptions={rangeOptions} value={dateRange} onValueChange={onDateRangeChange} />
          <Button label="Edit Costs" icon="pi pi-pencil" onClick={onEditConfig} />
        </div>
      }
    >
      {isUsageLoading && (
        <div className="flex justify-content-center p-6">
          <ProgressSpinner style={{ width: 50, height: 50 }} />
        </div>
      )}

      {!isUsageLoading && result && (
        <ReportContent result={result} />
      )}

      {!isUsageLoading && !result && (
        <AppCard>
          <p className="text-500 text-center">Select a date range to view your ROI report.</p>
        </AppCard>
      )}
    </AppPage>
  );
};

/* ===== Report Content (unlocked state) ===== */

interface AfterVendorGroup {
  vendor: string;
  products: string[];
  totalCost: number;
}

const ReportContent: FC<{ result: VoxRoiResult }> = ({ result }) => {
  const afterGroups: AfterVendorGroup[] = useMemo(() => {
    const map = new Map<string, AfterVendorGroup>();
    for (const line of result.after.lines) {
      const existing = map.get(line.vendor);
      if (existing) {
        existing.products.push(line.productDisplayName);
        existing.totalCost += line.lineTotal;
      } else {
        map.set(line.vendor, {
          vendor: line.vendor,
          products: [line.productDisplayName],
          totalCost: line.lineTotal,
        });
      }
    }
    return Array.from(map.values());
  }, [result.after.lines]);

  return (
    <>
      {/* ===== Savings Hero Card ===== */}
      <div className={styles.savingsHero}>
        <div className={styles.savingsHeroTitle}>Your Estimated Savings &middot; VoX ROI</div>
        <div className={styles.savingsHeroSubtitle}>
          {result.unitCount.toLocaleString('en-US')} units
          {result.savings.pct !== null && (
            <> &middot; cost per loan {result.savings.pct >= 0 ? 'down' : 'up'} {pct(Math.abs(result.savings.pct))}</>
          )}
        </div>

        <div className={styles.savingsHeroFigures}>
          <div className={styles.savingsHeroMetric}>
            <span className={styles.heroValue}>{usd(result.savings.total)}</span>
            <span className={styles.heroLabel}>Total Saved</span>
          </div>
          <div className={styles.savingsHeroMetric}>
            <span className={styles.heroValueSecondary}>{pct(result.savings.pct)}</span>
            <span className={styles.heroLabel}>Lower Cost Per Loan</span>
          </div>
          <div className={styles.savingsHeroMetric}>
            <span className={styles.heroValueSecondary}>{usd2(result.savings.perLoan)}</span>
            <span className={styles.heroLabel}>Saved Per Loan</span>
          </div>

          {/* Before → After cost-per-loan callout */}
          <div className={styles.costCallout}>
            <div className={styles.costCalloutLabel}>Avg Cost Per Loan</div>
            <div className={styles.costCalloutValues}>
              <span className={styles.costCalloutAmount}>{usd2(result.before.avgCostPerLoan)}</span>
              <span className={styles.costCalloutArrow}>&rarr;</span>
              <span className={styles.costCalloutAmount}>{usd2(result.after.avgCostPerLoan)}</span>
            </div>
          </div>
        </div>

        {/* Supporting pills */}
        <div className={styles.savingsPills}>
          <div className={styles.savingsPill}>
            <div className={styles.pillLabel}>Units</div>
            <div className={styles.pillValue}>{result.unitCount.toLocaleString('en-US')}</div>
          </div>
          <div className={styles.savingsPill}>
            <div className={styles.pillLabel}>Total Spend Before</div>
            <div className={styles.pillValue}>{usd(result.before.totalSpend)}</div>
          </div>
          <div className={styles.savingsPill}>
            <div className={styles.pillLabel}>Total Spend After</div>
            <div className={styles.pillValue}>{usd(result.after.totalSpend)}</div>
          </div>
        </div>
      </div>

      {/* ===== Before / After columns ===== */}
      <div className={`${styles.columns} mt-4`}>
        {/* BEFORE */}
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <div className={`${styles.sectionIcon} ${styles.sectionIconBefore}`}>
              <i className="pi pi-chart-bar" />
            </div>
            <span className={`${styles.sectionTitle} ${styles.sectionTitleBefore}`}>
              BEFORE
            </span>
            <span className={styles.sectionTitleSuffix}>(Pre-VoX / Pre-Mezzo)</span>
          </div>
          <div className={styles.sectionSubtitle}>Providers used before VoX</div>

          {result.before.providers.length > 0 ? (
            <table className={styles.providerTable}>
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Product</th>
                  <th>Cost per Order</th>
                </tr>
              </thead>
              <tbody>
                {result.before.providers.map((provider) => (
                  <tr key={provider.productKey}>
                    <td>{provider.vendor}</td>
                    <td>{provider.productDisplayName}</td>
                    <td>{provider.costPerOrder !== undefined ? usd2(provider.costPerOrder) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="mt-3">
              <Message severity="info" text="No prior providers configured" className="w-full" />
            </div>
          )}

          <p className={styles.routingNote}>
            Order volume intentionally omitted — the savings come from how Mezzo routes and places orders, not from cheaper provider contracts.
          </p>

          {/* Summary box */}
          <div className={styles.summaryBox}>
            <div className={styles.summaryRow}>
              <span className={styles.summaryTitle}>Summary (Before)</span>
              <span className={styles.summaryValue}>{result.before.providerCount} {result.before.providerCount === 1 ? 'Provider' : 'Providers'}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Average cost per order</span>
              <span className={styles.summaryValue}>{usd2(result.before.avgCostPerOrder)}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Avg cost per loan (Before)</span>
              <span className={styles.summaryValue}>{usd2(result.before.avgCostPerLoan)}</span>
            </div>
          </div>
        </div>

        {/* AFTER */}
        <div className={`${styles.sectionCard} ${styles.sectionCardAfter}`}>
          <div className={styles.sectionHeader}>
            <div className={`${styles.sectionIcon} ${styles.sectionIconAfter}`}>
              <i className="pi pi-check-circle" />
            </div>
            <span className={`${styles.sectionTitle} ${styles.sectionTitleAfter}`}>
              AFTER
            </span>
            <span className={styles.sectionTitleSuffix}>(With VoX / Mezzo)</span>
          </div>
          <div className={styles.sectionSubtitle}>Providers used with VoX</div>

          {afterGroups.length > 0 ? (
            <table className={`${styles.providerTable} ${styles.providerTableAfter}`}>
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Products</th>
                  <th>Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {afterGroups.map((group) => (
                  <tr key={group.vendor}>
                    <td>{group.vendor}</td>
                    <td>{group.products.join(', ')}</td>
                    <td>{usd2(group.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="mt-3">
              <Message severity="info" text="No usage data in selected period" className="w-full" />
            </div>
          )}

          {/* Summary box */}
          <div className={`${styles.summaryBox} ${styles.summaryBoxAfter}`}>
            <div className={styles.summaryRow}>
              <span className={`${styles.summaryTitle} ${styles.summaryTitleAfter}`}>Summary (After)</span>
              <span className={styles.summaryValueGreen}>{afterGroups.length} {afterGroups.length === 1 ? 'Provider' : 'Providers'}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Total actual spend</span>
              <span className={styles.summaryValue}>{usd2(result.after.totalSpend)}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Avg cost per loan (After)</span>
              <span className={styles.summaryValueGreen}>{usd2(result.after.avgCostPerLoan)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {result.warnings.unmappedUsage.length > 0 && (
        <Message
          severity="warn"
          className="mt-3 w-full"
          text={`${result.warnings.unmappedUsage.length} usage row(s) could not be mapped to a catalog product.`}
        />
      )}
      {result.warnings.productsMissingCost.length > 0 && (
        <Message
          severity="warn"
          className="mt-3 w-full"
          text={`Missing costs for: ${result.warnings.productsMissingCost.join(', ')}`}
        />
      )}
    </>
  );
};

export default VoxRoi;
